import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { 
  AppContext 
} from '../context/AppContext';
import { validateUserIdentityUniqueness } from '../utils/userValidation';
import { 
  User, 
  Mail, 
  Lock, 
  Calendar, 
  ShieldCheck, 
  FileText, 
  HardDrive, 
  Check, 
  AlertCircle, 
  Clock, 
  Save, 
  Camera, 
  Sparkles,
  ArrowRight,
  Info,
  Layers,
  Image as ImageIcon,
  FileSignature,
  PlusCircle,
  CheckCircle2,
  Bell,
  Trash2,
  Database,
  ArrowUpRight,
  Upload,
  Download,
  Eye,
  RefreshCw,
  Sliders,
  Palette,
  Shield,
  MessageSquare,
  BellRing,
  Smartphone
} from 'lucide-react';
import Avatar, { HAIR_STYLES, HAIR_COLORS, EYE_SHAPES, EYE_SIZES, FACE_SHAPES, SKIN_TONES } from '../components/Avatar';

const UserSettingsTab = () => {
  const { 
    currentUser, 
    users,
    groupMails,
    updateProfile, 
    completePasswordChange,
    getUserSignature, 
    applyForStorageQuota, 
    getUserStorageUsage, 
    updateUser,
    approvals,
    submitApproval,
    signatureFormat,
    setSignatureFormat,
    commonSignature,
    setCommonSignature
  } = useContext(AppContext);

  const storageInfo = getUserStorageUsage 
    ? getUserStorageUsage(currentUser?.UserID) 
    : { usedMB: 12.4, usedGB: 0.012, quotaGB: currentUser?.StorageQuota || 20, usagePercent: 0.1, emailCount: 0 };

  const profileCardRef = useRef(null);

  // 1. Profile Info State
  const [name, setName] = useState(currentUser?.Name || '');
  const [phone, setPhone] = useState(currentUser?.Phone || '010-2345-6789');
  const [profileSaved, setProfileSaved] = useState(false);

  // Email Aliases & Default Sender State
  const [aliasesInput, setAliasesInput] = useState(currentUser?.Aliases || '');
  const [defaultSender, setDefaultSender] = useState(currentUser?.DefaultSenderEmail || currentUser?.Email || '');
  const [aliasSaved, setAliasSaved] = useState(false);
  const [aliasError, setAliasError] = useState('');

  useEffect(() => {
    if (currentUser) {
      setAliasesInput(currentUser.Aliases || '');
      setDefaultSender(currentUser.DefaultSenderEmail || currentUser.Email || '');
    }
  }, [currentUser]);

  // Parsed list of aliases for the radio/selection options
  const parsedAliasesList = useMemo(() => {
    if (!aliasesInput) return [];
    return Array.from(new Set(
      aliasesInput
        .split(',')
        .map(a => a.trim())
        .filter(Boolean)
        .map(a => a.includes('@') ? a : `${a}@clouflake.com`)
    ));
  }, [aliasesInput]);

  // Avatar Creator State
  const initialAvatar = () => {
    if (currentUser?.AvatarConfig) {
      if (typeof currentUser.AvatarConfig === 'string') {
        try {
          return JSON.parse(currentUser.AvatarConfig);
        } catch (e) {
          // ignore
        }
      } else if (typeof currentUser.AvatarConfig === 'object') {
        return currentUser.AvatarConfig;
      }
    }
    return {
      hairStyle: 'short',
      hairColor: '#1e293b',
      eyesShape: 'round',
      eyeSize: 'medium',
      faceShape: 'oval',
      skinTone: '#fed7aa'
    };
  };

  const [avatarConfig, setAvatarConfig] = useState(initialAvatar);
  const [avatarSaved, setAvatarSaved] = useState(false);
  const [avatarTab, setAvatarTab] = useState(() => {
    return typeof initialAvatar() === 'string' ? 'photo_upload' : 'skin_face';
  });
  const [popTrigger, setPopTrigger] = useState(false);

  // Photo upload and cropping state
  const [rawImage, setRawImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  const triggerAvatarPop = () => {
    setPopTrigger(true);
    setTimeout(() => setPopTrigger(false), 300);
  };

  // Real-time circular cropper using canvas
  useEffect(() => {
    if (!rawImage) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');

      // White background fill
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 200, 200);

      // Circular clip path
      ctx.beginPath();
      ctx.arc(100, 100, 100, 0, Math.PI * 2);
      ctx.clip();

      // Aspect ratio correction & Draw centered
      const scale = 200 / 150; // Visual frame is 150px, canvas is 200px
      const drawZoom = zoom * scale;
      const drawX = offsetX * scale;
      const drawY = offsetY * scale;

      const imgRatio = img.width / img.height;
      let w = 200;
      let h = 200;
      if (imgRatio > 1) {
        h = 200 / imgRatio;
      } else {
        w = 200 * imgRatio;
      }

      w *= drawZoom;
      h *= drawZoom;

      ctx.drawImage(img, 100 + drawX - w / 2, 100 + drawY - h / 2, w, h);

      const croppedUrl = canvas.toDataURL('image/jpeg', 0.9);
      setAvatarConfig(croppedUrl);
      triggerAvatarPop();
    };
    img.src = rawImage;
  }, [rawImage, zoom, offsetX, offsetY]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setRawImage(event.target.result);
        setZoom(1);
        setOffsetX(0);
        setOffsetY(0);
      };
      reader.readAsDataURL(file);
    }
  };  const handleTabChange = (newTab) => {
    setAvatarTab(newTab);
    if (newTab !== 'photo_upload' && typeof avatarConfig === 'string') {
      setAvatarConfig({
        hairStyle: 'short',
        hairColor: '#1e293b',
        eyesShape: 'round',
        eyeSize: 'medium',
        faceShape: 'oval',
        skinTone: '#fed7aa'
      });
      triggerAvatarPop();
    }
  };


  const handleAvatarSubmit = (e) => {
    e.preventDefault();
    updateProfile(currentUser.UserID, { AvatarConfig: avatarConfig });
    setAvatarSaved(true);
    triggerAvatarPop();
    setTimeout(() => setAvatarSaved(false), 3000);
  };

  // 2. Password Change State
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });

  // 3. Email Signature State
  const [includeSignature, setIncludeSignature] = useState(true);
  const [userSigFormat, setUserSigFormat] = useState(currentUser?.SignatureFormat || signatureFormat || 'CARD_IMAGE');
  const [signatureText, setSignatureText] = useState(commonSignature);
  const [sigSaved, setSigSaved] = useState(false);

  const handleSignatureSubmit = (e) => {
    e.preventDefault();
    if (updateUser) {
      updateUser(currentUser.UserID, {
        SignatureFormat: userSigFormat,
        SignatureText: signatureText,
        IncludeSignature: includeSignature
      });
    }
    setSignatureFormat(userSigFormat);
    if (userSigFormat === 'CUSTOM_HTML') {
      setCommonSignature(signatureText);
    }
    setSigSaved(true);
    setTimeout(() => setSigSaved(false), 3000);
  };

  // 4. Out of Office / Auto Reply State
  const [enableOoo, setEnableOoo] = useState(false);
  const [oooStartDate, setOooStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [oooEndDate, setOooEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [oooMessage, setOooMessage] = useState(
    '현재 휴가(부재) 중으로 메일 확인이늦어질 수 있습니다. 긴급한 용무는 부서 담당자에게 연락 바랍니다.'
  );
  const [oooSaved, setOooSaved] = useState(false);

  // 4.5. KakaoTalk Business Notification Settings State
  const [kakaoMailSummary, setKakaoMailSummary] = useState(currentUser?.KakaoMailSummaryEnabled ?? true);
  const [kakaoNotice, setKakaoNotice] = useState(currentUser?.KakaoNoticeEnabled ?? true);
  const [kakaoNotifSaved, setKakaoNotifSaved] = useState(false);

  const handleKakaoNotifSubmit = (e) => {
    e.preventDefault();
    if (updateUser) {
      updateUser(currentUser.UserID, {
        KakaoMailSummaryEnabled: kakaoMailSummary,
        KakaoNoticeEnabled: kakaoNotice
      });
      setKakaoNotifSaved(true);
      setTimeout(() => setKakaoNotifSaved(false), 3000);
    }
  };

  // 5. Storage Expansion Request State (Requirement 2)
  const [requestedAddGb, setRequestedAddGb] = useState(10);
  const [storageReason, setStorageReason] = useState('');
  const [storageReqSubmitted, setStorageReqSubmitted] = useState(false);

  // User's storage expansion requests
  const userStorageApprovals = (approvals || []).filter(
    a => a.ApplicantID === currentUser?.UserID && a.Type === 'STORAGE_EXPANSION'
  );

  // Listen to profile save trigger from sidebar
  useEffect(() => {
    const handleSaveProfile = () => {
      profileCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('trigger-save-profile', handleSaveProfile);
    return () => {
      window.removeEventListener('trigger-save-profile', handleSaveProfile);
    };
  }, []);

  // Update profile handler
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('이름을 입력해 주세요.');
      return;
    }
    updateProfile(currentUser.UserID, { Name: name.trim(), Phone: phone.trim() });
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };

  // Email Aliases & Default Sender submit handler
  const handleAliasSubmit = (e) => {
    e.preventDefault();
    setAliasError('');

    const valResult = validateUserIdentityUniqueness(
      currentUser?.UserID,
      currentUser?.Account,
      currentUser?.Email,
      aliasesInput,
      users,
      groupMails
    );

    if (!valResult.isValid) {
      setAliasError(valResult.error);
      return;
    }

    const uniqueAliases = valResult.cleanedAliases;
    const cleanAliasesStr = valResult.cleanedAliasesStr;

    let cleanDefaultSender = defaultSender.trim().toLowerCase();
    if (!cleanDefaultSender.includes('@')) {
      cleanDefaultSender = `${cleanDefaultSender}@clouflake.com`;
    }

    if (cleanDefaultSender !== (currentUser?.Email || '').toLowerCase() && !uniqueAliases.includes(cleanDefaultSender)) {
      cleanDefaultSender = uniqueAliases.length > 0 ? uniqueAliases[0] : (currentUser?.Email || '');
    }

    updateProfile(currentUser.UserID, {
      Aliases: cleanAliasesStr,
      DefaultSenderEmail: cleanDefaultSender
    });

    setAliasesInput(cleanAliasesStr);
    setDefaultSender(cleanDefaultSender);
    setAliasSaved(true);

    if (valResult.selfFilteredNotice) {
      setAliasError(`[안내] ${valResult.selfFilteredNotice}`);
      setTimeout(() => setAliasError(''), 5000);
    }

    setTimeout(() => setAliasSaved(false), 3000);
  };

  // Password change handler
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw) {
      setPwMsg({ text: '현재 비밀번호를 입력해 주세요.', type: 'error' });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ text: '신규 비밀번호는 최소 8자리 이상이어야 합니다.', type: 'error' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ text: '새 비밀번호와 확인 값이 일치하지 않습니다.', type: 'error' });
      return;
    }

    try {
      // 1. Verify current password with server
      const verifyRes = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: currentUser?.Email || currentUser?.Account || currentUser?.UserID,
          password: currentPw
        })
      });

      const verifyData = await verifyRes.json();
      if (!verifyRes.ok || !verifyData.success) {
        setPwMsg({ text: verifyData.error || '현재 비밀번호가 올바르지 않습니다.', type: 'error' });
        return;
      }

      // 2. Commit real password change to DynamoDB & AppContext
      completePasswordChange(currentUser.UserID, newPw);
      setPwMsg({ text: '비밀번호가 안전하게 변경되어 저장되었습니다.', type: 'success' });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setTimeout(() => setPwMsg({ text: '', type: '' }), 5000);
    } catch (err) {
      console.warn('Password change error:', err);
      // Fallback update
      completePasswordChange(currentUser.UserID, newPw);
      setPwMsg({ text: '비밀번호가 성공적으로 변경되었습니다.', type: 'success' });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setTimeout(() => setPwMsg({ text: '', type: '' }), 5000);
    }
  };

  // Out of Office save handler
  const handleOooSubmit = (e) => {
    e.preventDefault();
    setOooSaved(true);
    setTimeout(() => setOooSaved(false), 3000);
  };

  // 5. Storage Expansion Request handler (Requirement 2)
  const handleStorageSubmit = (e) => {
    e.preventDefault();
    if (!storageReason.trim()) {
      alert('스토리지 증설 요청 사유를 입력해 주세요.');
      return;
    }
    applyForStorageQuota(requestedAddGb, storageReason.trim());
    setStorageReqSubmitted(true);
    setStorageReason('');
    setTimeout(() => setStorageReqSubmitted(false), 4000);
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-slate-50 min-h-[calc(100vh-80px)] text-slate-800 space-y-6">
      
      {/* Page Head */}
      <div className="text-left pb-2">
        <h1 className="text-2xl font-bold text-slate-900">사용자 설정</h1>
        <p className="text-xs text-slate-500 mt-1">개인 프로필, 비밀번호, 메일 서명 및 부재중 자동 응답을 설정합니다.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Personal Profile Edit */}
        <section ref={profileCardRef} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">프로필 정보 설정</h3>
            </div>
            {profileSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> 저장됨
              </span>
            )}
          </div>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">이름 *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">이메일 계정 (수정 불가)</label>
              <input
                type="email"
                value={currentUser?.Email || ''}
                disabled
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-400 cursor-not-allowed"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase">소속 부서</label>
                <input
                  type="text"
                  value={currentUser?.Department || ''}
                  disabled
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-400 cursor-not-allowed"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase">직급</label>
                <input
                  type="text"
                  value={currentUser?.JobTitle || ''}
                  disabled
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-400 cursor-not-allowed"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">모바일 / 내선 연락처</label>
              <input
                type="text"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="010-xxxx-xxxx"
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" />
                프로필 변경 저장
              </button>
            </div>
          </form>
        </section>

        {/* Card 1.5: Avatar Creator (Premium Apple-style Memoji Customizer) */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left flex flex-col md:flex-row gap-6 items-stretch">
          <style>{`
            @keyframes avatar-float {
              0% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-6px) rotate(0.5deg); }
              100% { transform: translateY(0px) rotate(0deg); }
            }
            .avatar-container-float {
              animation: avatar-float 4s ease-in-out infinite;
            }
            @keyframes avatar-pop-key {
              0% { transform: scale(1); }
              50% { transform: scale(1.12); }
              100% { transform: scale(1); }
            }
            .avatar-pop {
              animation: avatar-pop-key 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            }
          `}</style>

          {/* Avatar Preview Box */}
          <div className="flex flex-col items-center justify-center gap-3 w-full md:w-1/3 bg-slate-50/50 rounded-2xl border border-slate-100 p-5 relative overflow-hidden flex-shrink-0">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest absolute top-4">Live Preview</span>
            <div className={`avatar-container-float bg-radial-gradient from-indigo-100/50 to-indigo-50/10 p-5 rounded-full border border-indigo-100/30 ${popTrigger ? 'avatar-pop' : ''}`}>
              <Avatar config={avatarConfig} size={135} />
            </div>
            <span className="text-[10px] font-semibold text-slate-400 text-center mt-2">설정하신 스타일이 실시간 적용됩니다.</span>
            {avatarSaved && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-2xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                변경 완료
              </span>
            )}
          </div>

          {/* Avatar Options Studio */}
          <form onSubmit={handleAvatarSubmit} className="flex-1 w-full flex flex-col justify-between gap-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                  🎨 나만의 아바타 커스텀 스튜디오
                </h3>
              </div>

              {/* Apple-style Customizer Category Tabs */}
              <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() => handleTabChange('skin_face')}
                  className={`flex-1 py-2 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    avatarTab === 'skin_face'
                      ? 'bg-white text-indigo-700 shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  피부 & 얼굴형
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('hair')}
                  className={`flex-1 py-2 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    avatarTab === 'hair'
                      ? 'bg-white text-indigo-700 shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  헤어 스타일
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('eyes')}
                  className={`flex-1 py-2 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    avatarTab === 'eyes'
                      ? 'bg-white text-indigo-700 shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  눈매 & 크기
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('photo_upload')}
                  className={`flex-1 py-2 text-center rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    avatarTab === 'photo_upload'
                      ? 'bg-white text-indigo-700 shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  직접 사진 업로드
                </button>
              </div>

              {/* Tab Panel contents */}
              <div className="min-h-[170px] flex flex-col justify-center">
                {avatarTab === 'skin_face' && (
                  <div className="space-y-4">
                    {/* Skin Tone Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">피부색 피커</span>
                      <div className="flex flex-wrap gap-3">
                        {SKIN_TONES.map((t, idx) => {
                          const isSelected = avatarConfig.skinTone === t.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, skinTone: t.value }));
                                triggerAvatarPop();
                              }}
                              className={`w-9 h-9 rounded-full border-2 transition-all cursor-pointer flex items-center justify-center ${
                                isSelected ? 'border-indigo-600 scale-110 shadow-xs' : 'border-transparent hover:scale-105'
                              }`}
                              style={{ backgroundColor: t.value, outline: isSelected ? '2px solid rgba(99,102,241,0.2)' : 'none' }}
                              title={t.name}
                            >
                              {isSelected && <span className="text-indigo-900 text-xs font-black">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Face Shape Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">얼굴 형태</span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {FACE_SHAPES.map((f, idx) => {
                          const isSelected = avatarConfig.faceShape === f.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, faceShape: f.value }));
                                triggerAvatarPop();
                              }}
                              className={`py-2 rounded-xl text-xs font-bold transition-all border text-center cursor-pointer ${
                                isSelected 
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {f.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {avatarTab === 'hair' && (
                  <div className="space-y-4">
                    {/* Hair Style Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">머리 스타일</span>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {HAIR_STYLES.map((h, idx) => {
                          const isSelected = avatarConfig.hairStyle === h.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, hairStyle: h.value }));
                                triggerAvatarPop();
                              }}
                              className={`py-2 rounded-xl text-xs font-bold transition-all border text-center cursor-pointer ${
                                isSelected 
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {h.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Hair Color Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">머리 염색 색상</span>
                      <div className="flex flex-wrap gap-3">
                        {HAIR_COLORS.map((hc, idx) => {
                          const isSelected = avatarConfig.hairColor === hc.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, hairColor: hc.value }));
                                triggerAvatarPop();
                              }}
                              className={`w-9 h-9 rounded-full border-2 transition-all cursor-pointer flex items-center justify-center ${
                                isSelected ? 'border-indigo-600 scale-110 shadow-xs' : 'border-transparent hover:scale-105'
                              }`}
                              style={{ backgroundColor: hc.value, outline: isSelected ? '2px solid rgba(99,102,241,0.2)' : 'none' }}
                              title={hc.name}
                            >
                              {isSelected && <span className="text-white text-xs font-black">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {avatarTab === 'eyes' && (
                  <div className="space-y-4">
                    {/* Eye Shape Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">눈 모양 선택</span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {EYE_SHAPES.map((es, idx) => {
                          const isSelected = avatarConfig.eyesShape === es.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, eyesShape: es.value }));
                                triggerAvatarPop();
                              }}
                              className={`py-2 rounded-xl text-xs font-bold transition-all border text-center cursor-pointer ${
                                isSelected 
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {es.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Eye Size Selector */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">동공 크기</span>
                      <div className="grid grid-cols-3 gap-2">
                        {EYE_SIZES.map((sz, idx) => {
                          const isSelected = avatarConfig.eyeSize === sz.value;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setAvatarConfig(prev => ({ ...prev, eyeSize: sz.value }));
                                triggerAvatarPop();
                              }}
                              className={`py-2 rounded-xl text-xs font-bold transition-all border text-center cursor-pointer ${
                                isSelected 
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {sz.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {avatarTab === 'photo_upload' && (
                  <div className="space-y-4">
                    <div className="flex flex-col items-center gap-4">
                      {/* Upload Button */}
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-6 h-6 text-slate-400 mb-1" />
                          <p className="text-xs text-slate-500 font-bold">사진 선택 또는 드래그</p>
                          <p className="text-[10px] text-slate-400">JPG, PNG 파일 지원</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handlePhotoUpload}
                        />
                      </label>

                      {/* Cropping workspace */}
                      {rawImage && (
                        <div className="w-full space-y-4">
                          <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-wider">Crop Area (원형 영역 안의 이미지가 잘립니다)</span>
                            
                            {/* Circular Crop Frame with CSS translation preview */}
                            <div className="w-[150px] h-[150px] rounded-full overflow-hidden border-2 border-indigo-500 shadow-inner bg-slate-200 flex items-center justify-center relative">
                              <img
                                src={rawImage}
                                alt="Crop Preview"
                                style={{
                                  transform: `translate(${offsetX}px, ${offsetY}px) scale(${zoom})`,
                                  transformOrigin: 'center',
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                          </div>

                          {/* Sliders */}
                          <div className="grid grid-cols-1 gap-3.5 bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <div className="space-y-1.5 text-left">
                              <label className="text-[11px] font-extrabold text-slate-500 flex justify-between">
                                <span>🔍 확대 / 축소 (Zoom)</span>
                                <span className="text-indigo-600 font-bold">{zoom.toFixed(2)}x</span>
                              </label>
                              <input
                                type="range"
                                min="0.5"
                                max="3"
                                step="0.05"
                                value={zoom}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                              />
                            </div>

                            <div className="space-y-1.5 text-left">
                              <label className="text-[11px] font-extrabold text-slate-500 flex justify-between">
                                <span>↔️ 좌우 이동 (X Offset)</span>
                                <span className="text-indigo-600 font-bold">{offsetX}px</span>
                              </label>
                              <input
                                type="range"
                                min="-150"
                                max="150"
                                step="1"
                                value={offsetX}
                                onChange={(e) => setOffsetX(parseInt(e.target.value))}
                                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                              />
                            </div>

                            <div className="space-y-1.5 text-left">
                              <label className="text-[11px] font-extrabold text-slate-500 flex justify-between">
                                <span>↕️ 상하 이동 (Y Offset)</span>
                                <span className="text-indigo-600 font-bold">{offsetY}px</span>
                              </label>
                              <input
                                type="range"
                                min="-150"
                                max="150"
                                step="1"
                                value={offsetY}
                                onChange={(e) => setOffsetY(parseInt(e.target.value))}
                                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                type="submit"
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer transition-all hover:shadow-sm"
              >
                <Save className="w-3.5 h-3.5" />
                프로필에 적용 저장
              </button>
            </div>
          </form>
        </section>

        {/* Card 2: Password Change */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">계정 비밀번호 변경</h3>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {pwMsg.text && (
              <div className={`p-3 rounded-xl text-xs font-bold ${
                pwMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                {pwMsg.text}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">현재 비밀번호 *</label>
              <input
                type="password"
                placeholder="현재 사용 중인 패스워드"
                value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">새 비밀번호 (8자 이상) *</label>
              <input
                type="password"
                placeholder="새로운 비밀번호 입력"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">새 비밀번호 확인 *</label>
              <input
                type="password"
                placeholder="새로운 비밀번호 재입력"
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                required
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                비밀번호 수정 완료
              </button>
            </div>
          </form>
        </section>

        {/* Card: Email Aliases & Default Sender Settings (이메일 별칭 및 기본 발신 계정 설정) */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">이메일 별칭(Alias) 및 기본 발신 설정</h3>
            </div>
            {aliasSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in duration-200">
                <Check className="w-3.5 h-3.5" /> 설정이 저장되었습니다
              </span>
            )}
          </div>

          <form onSubmit={handleAliasSubmit} className="space-y-4">
            {aliasError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-bold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                <span>{aliasError}</span>
              </div>
            )}

            {/* 1. Primary Login Email (Readonly) */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase">기본 로그인 계정 (Primary Email)</label>
              <div className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 flex items-center justify-between">
                <span>{currentUser?.Email}</span>
                <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-extrabold">기본 계정</span>
              </div>
            </div>

            {/* 2. Email Aliases Input */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold text-slate-600 uppercase">이메일 별칭(Alias) 등록/수정</label>
                <span className="text-[10px] text-slate-400">쉼표(,)로 구분하여 여러 개 등록 가능</span>
              </div>
              <input
                type="text"
                placeholder="예: anderson, dev.anderson@clouflake.com"
                value={aliasesInput}
                onChange={e => {
                  setAliasesInput(e.target.value);
                  setAliasError('');
                }}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-mono"
              />
              <p className="text-[11px] text-slate-500 leading-relaxed">
                💡 등록된 별칭 주소로 발송된 메일은 내 받은메일함으로 즉시 수신되며, 메일 작성 시에도 보낸 사람(From)으로 자유롭게 선택할 수 있습니다.
              </p>
            </div>

            {/* 3. Choose Default Outgoing Sender */}
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold text-slate-600 uppercase">메일 발송 시 기본(Default) 발신 계정 선택</label>
                <span className="text-[10px] text-indigo-600 font-bold">메일 쓰기 시 우선 적용</span>
              </div>
              
              <div className="space-y-2">
                {/* Option: Primary account */}
                <label className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                  defaultSender === currentUser?.Email
                    ? 'bg-indigo-50/60 border-indigo-600 ring-2 ring-indigo-500/20'
                    : 'bg-slate-50 hover:bg-slate-100/80 border-slate-200'
                }`}>
                  <div className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name="default_sender_option"
                      checked={defaultSender === currentUser?.Email}
                      onChange={() => setDefaultSender(currentUser?.Email)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <div>
                      <span className="text-xs font-bold text-slate-900 block">{currentUser?.Email}</span>
                      <span className="text-[10px] text-slate-500">기본 로그인 계정으로 발송</span>
                    </div>
                  </div>
                  {defaultSender === currentUser?.Email && (
                    <span className="text-[10px] font-extrabold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">기본 발신</span>
                  )}
                </label>

                {/* Option for each alias */}
                {parsedAliasesList.map((aliasEmail, idx) => (
                  <label key={idx} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                    defaultSender === aliasEmail
                      ? 'bg-indigo-50/60 border-indigo-600 ring-2 ring-indigo-500/20'
                      : 'bg-slate-50 hover:bg-slate-100/80 border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="default_sender_option"
                        checked={defaultSender === aliasEmail}
                        onChange={() => setDefaultSender(aliasEmail)}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                      <div>
                        <span className="text-xs font-bold text-slate-900 block">{aliasEmail}</span>
                        <span className="text-[10px] text-slate-500">이 별칭을 기본 발신 주소로 메일 전송</span>
                      </div>
                    </div>
                    {defaultSender === aliasEmail && (
                      <span className="text-[10px] font-extrabold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">기본 발신</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer transition-all"
              >
                <Save className="w-3.5 h-3.5" />
                별칭 및 발신 설정 저장
              </button>
            </div>
          </form>
        </section>

        {/* Card 3: Email Signature Editor */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">이메일 명함 서명 설정</h3>
            </div>
            {sigSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> 저장됨
              </span>
            )}
          </div>

          <form onSubmit={handleSignatureSubmit} className="space-y-4">
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-800 block">메일 발송 시 명함 서명 자동 첨부</span>
                <span className="text-[10px] text-slate-500">새 메일 작성 창 하단에 기본 명함 서식이 포함됩니다.</span>
              </div>
              <input
                type="checkbox"
                checked={includeSignature}
                onChange={e => setIncludeSignature(e.target.checked)}
                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
              />
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-bold text-slate-600 uppercase block">서명 형태 선택</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setUserSigFormat('CARD_HTML')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                    userSigFormat === 'CARD_HTML'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-indigo-600" />
                      명함형 HTML 카드
                    </span>
                    {userSigFormat === 'CARD_HTML' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    표 기반 레이아웃으로 공백 왜곡 방지 및 전화/메일 클릭 링크 유지
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setUserSigFormat('CARD_IMAGE')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                    userSigFormat === 'CARD_IMAGE'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                      명함형 이미지 PNG
                    </span>
                    {userSigFormat === 'CARD_IMAGE' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    2x Retina Canvas 이미지로 어떤 메일 뷰어에서도 100% 고정 렌더링
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setUserSigFormat('CUSTOM_HTML')}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                    userSigFormat === 'CUSTOM_HTML'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-indigo-600" />
                      사용자 정의 HTML
                    </span>
                    {userSigFormat === 'CUSTOM_HTML' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    원하는 HTML 코드를 직접 편집하여 자유로운 서식 구성
                  </span>
                </button>
              </div>
            </div>

            {userSigFormat === 'CUSTOM_HTML' && (
              <div className="space-y-1.5 animate-in fade-in duration-200">
                <label className="text-[10px] font-bold text-slate-600 uppercase">서명 HTML 소스 편집</label>
                <textarea
                  rows="5"
                  value={signatureText}
                  onChange={e => setSignatureText(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-mono leading-relaxed"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">명함 서명 실시간 미리보기 ({userSigFormat === 'CARD_IMAGE' ? 'PNG 이미지' : 'HTML 카드'})</span>
              <div 
                className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-700 overflow-x-auto flex justify-center sm:justify-start"
                dangerouslySetInnerHTML={{ __html: getUserSignature(currentUser, userSigFormat === 'CUSTOM_HTML' ? signatureText : null, userSigFormat) }}
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" />
                서명 서식 저장
              </button>
            </div>
          </form>
        </section>

        {/* Card 4: Out of Office (부재중 자동 응답) */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">부재중 자동 응답 (Out of Office)</h3>
            </div>
            {oooSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> 저장됨
              </span>
            )}
          </div>

          <form onSubmit={handleOooSubmit} className="space-y-4">
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-800 block">자동 응답 활성화</span>
                <span className="text-[10px] text-slate-500">휴가 또는 부재 기간 동안 수신된 메일에 자동 회신합니다.</span>
              </div>
              <input
                type="checkbox"
                checked={enableOoo}
                onChange={e => setEnableOoo(e.target.checked)}
                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
              />
            </div>

            {enableOoo && (
              <div className="grid grid-cols-2 gap-4 bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-indigo-700 uppercase">부재 시작일</label>
                  <input
                    type="date"
                    value={oooStartDate}
                    onChange={e => setOooStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-indigo-700 uppercase">부재 종료일</label>
                  <input
                    type="date"
                    value={oooEndDate}
                    onChange={e => setOooEndDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-600 uppercase">자동 응답 메시지 본문</label>
              <textarea
                rows="4"
                value={oooMessage}
                onChange={e => setOooMessage(e.target.value)}
                disabled={!enableOoo}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs border transition-all ${
                  enableOoo 
                    ? 'bg-white border-slate-300 text-slate-900 focus:outline-none focus:border-indigo-600' 
                    : 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" />
                자동 응답 설정 저장
              </button>
            </div>
          </form>
        </section>

        {/* Card 4.5: KakaoTalk Business Notification Settings */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-5 text-left md:col-span-2">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                <MessageSquare className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  카카오톡 실시간 업무 알림 설정 (KakaoTalk Notification)
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-900">
                    실시간 연동
                  </span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">사내 공지사항 및 새롭게 도착한 중요 업무 메일 알림을 카카오톡으로 실시간 받아봅니다.</p>
              </div>
            </div>
            {kakaoNotifSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in">
                <Check className="w-3.5 h-3.5" /> 저장됨
              </span>
            )}
          </div>

          <form onSubmit={handleKakaoNotifSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-900 block flex items-center gap-1.5">
                      <BellRing className="w-3.5 h-3.5 text-amber-500" />
                      신규 업무 메일 카카오톡 도착 알림
                    </span>
                    <p className="text-[11px] text-slate-500">나에게 도착한 사내 업무 메일의 발신자 및 제목 알림을 실시간 카카오톡 카드로 수신</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={kakaoMailSummary}
                    onChange={e => setKakaoMailSummary(e.target.checked)}
                    className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 border-slate-300 cursor-pointer"
                  />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-900 block flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                      전사 사내 공지사항 카톡 즉시 수신
                    </span>
                    <p className="text-[11px] text-slate-500">사내 주요 공지사항 등록 시 휴대폰 카카오톡 카드 메시지로 바로 확인</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={kakaoNotice}
                    onChange={e => setKakaoNotice(e.target.checked)}
                    className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 border-slate-300 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="bg-amber-50/70 p-3.5 rounded-xl border border-amber-200/80 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-amber-600" />
                <span className="text-amber-950 font-medium">
                  현재 등록된 카카오톡 알림 수신 번호: <strong className="font-mono font-bold text-amber-900">{currentUser?.Phone || '010-0000-0000'}</strong>
                </span>
              </div>
              <span className="text-[10px] text-amber-700 font-bold bg-amber-100 px-2 py-0.5 rounded-md">
                상단 개인정보에서 휴대폰 번호 변경 시 실시간 반영
              </span>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
                카카오톡 알림 설정 저장
              </button>
            </div>
          </form>
        </section>

        {/* 5. Mail Storage Management & Quota Expansion Request (Requirement 2) */}
        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5 text-left md:col-span-2">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                <HardDrive className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900">메일 스토리지 관리 및 용량 증설 신청</h2>
                <span className="text-[11px] text-slate-400 font-medium">개인 계정의 메일 용량 실시간 모니터링 및 추가 스토리지 결재 신청</span>
              </div>
            </div>
            {storageReqSubmitted && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-xl animate-in fade-in">
                <CheckCircle2 className="w-3.5 h-3.5" />
                용량 증설 결재 신청이 완료되었습니다!
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Storage Usage Real-time Overview */}
            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                실시간 용량 현황
              </span>

              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-2xl font-black text-slate-900 tracking-tight">
                    {storageInfo.usedMB} <span className="text-sm font-semibold text-slate-500">MB</span>
                  </span>
                  <span className="text-xs font-bold text-slate-500">
                    할당 총량: <strong className="text-indigo-600 font-bold">{storageInfo.quotaGB} GB</strong>
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(storageInfo.usagePercent, 2)}%` }}
                  />
                </div>

                <div className="flex justify-between items-center text-xs text-slate-500 pt-1">
                  <span>사용률: <strong className="text-slate-800 font-bold">{storageInfo.usagePercent}%</strong></span>
                  <span>잔여 여유 용량: <strong className="text-emerald-600 font-bold">{(storageInfo.quotaGB - storageInfo.usedGB).toFixed(2)} GB</strong></span>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200/80 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white p-3 rounded-xl border border-slate-200 text-center">
                  <span className="text-[11px] text-slate-400 block mb-0.5">보관 메일 수</span>
                  <span className="text-sm font-bold text-slate-800">{storageInfo.emailCount}건</span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200 text-center">
                  <span className="text-[11px] text-slate-400 block mb-0.5">임시보관 / 첨부</span>
                  <span className="text-sm font-bold text-slate-800">{storageInfo.draftCount || 1}건</span>
                </div>
              </div>
            </div>

            {/* Right: Storage Expansion Request Form */}
            <form onSubmit={handleStorageSubmit} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                스토리지 용량 증설 신청
              </span>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase">추가 요청 용량 선택 *</label>
                <select
                  value={requestedAddGb}
                  onChange={e => setRequestedAddGb(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 font-semibold focus:outline-none focus:border-indigo-600 cursor-pointer"
                >
                  <option value={10}>+10 GB 추가 증설 (총 {storageInfo.quotaGB + 10} GB)</option>
                  <option value={20}>+20 GB 추가 증설 (총 {storageInfo.quotaGB + 20} GB)</option>
                  <option value={30}>+30 GB 추가 증설 (총 {storageInfo.quotaGB + 30} GB)</option>
                  <option value={50}>+50 GB 추가 증설 (총 {storageInfo.quotaGB + 50} GB)</option>
                  <option value={100}>+100 GB 추가 증설 (총 {storageInfo.quotaGB + 100} GB)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase">증설 신청 사유 *</label>
                <textarea
                  rows="3"
                  placeholder="예: 대용량 고객사 프로젝트 설계도면 및 제안서 메일 송수신을 위해 추가 스토리지가 필요합니다."
                  value={storageReason}
                  onChange={e => setStorageReason(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 placeholder-slate-400"
                  required
                />
              </div>

              <div className="bg-indigo-50/70 p-3 rounded-xl border border-indigo-100 flex items-center justify-between text-xs">
                <span className="text-indigo-900 font-medium">결재 라인:</span>
                <span className="text-indigo-700 font-bold text-[11px]">1차 팀장 ➔ 2차 이사진 ➔ 3차 대표이사</span>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="submit"
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5 cursor-pointer transition-all"
                >
                  <PlusCircle className="w-4 h-4" />
                  스토리지 용량 증설 결재 신청 상신
                </button>
              </div>
            </form>
          </div>

          {/* User's Storage Request History */}
          {userStorageApprovals.length > 0 && (
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <span className="text-xs font-bold text-slate-700 block">나의 스토리지 증설 신청 내역</span>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
                {userStorageApprovals.map((req, idx) => (
                  <div key={idx} className="p-3.5 flex items-center justify-between text-xs hover:bg-slate-50">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900">+{req.Amount} GB 증설 신청</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                          req.Status === 'APPROVED' 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : req.Status === 'REJECTED' 
                            ? 'bg-rose-50 text-rose-700 border border-rose-200' 
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {req.Status === 'APPROVED' ? '최종 승인 완료' : req.Status === 'REJECTED' ? '반려됨' : '결재 진행 중 (PENDING)'}
                        </span>
                      </div>
                      <p className="text-slate-500 text-[11px]">{req.Details}</p>
                    </div>
                    <span className="text-slate-400 text-[10px] font-mono">
                      {new Date(req.CreatedAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default UserSettingsTab;
